import { consume } from '@lit/context';
import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { selectorTooltip } from '../../../theme/selector.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Cruise Selector - Displays cruise mode toggle button
 *
 * Desktop only — on mobile, cruise lives in the bottom-right `touch-controls`
 * cluster instead (airmash-game renders this sidebar only when not mobile).
 *
 * Shows on the left of the game screen with:
 * - 44px clickable button
 * - A single `circle-gauge` icon, mask-recoloured by CSS: tertiary grey when
 *   idle, neon green (`--color-cruise-active`) when cruise mode is on — one SVG
 *   for both states instead of the legacy two-file off/on pair
 * - Tooltip with description
 *
 * @element cruise-selector
 */
@customElement('cruise-selector')
export class CruiseSelector extends LitElement {
  static override styles = [
    tokens,
    selectorTooltip,
    css`
      :host {
        display: block;
        width: 44px;
        pointer-events: auto;
        /* selectorTooltip defaults to 180px; cruise's card carries more text. */
        --tooltip-width: 200px;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow click on the button or its card
           shouldn't select the text. Same set as aircraft- and upgrade-selector. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      .cruise {
        width: 44px;
        height: 44px;
        background-color: var(--color-bg-selector);
        cursor: pointer;
        position: relative;
        border-radius: var(--radius-md);
        /* Harmless on desktop; keeps taps crisp on touch-capable screens —
           kills iOS double-tap-zoom hesitation and the grey tap flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      /* The cruise glyph, drawn as a mask so a single SVG recolours per state
         (idle grey / active green) — no more two-file off/on pair. Same
         mask-recolour pattern as the CTF flag in game-log and the slider-reset
         icon. Decorative: clicks pass through to .cruise. */
      .cruise .icon {
        position: absolute;
        inset: 0;
        pointer-events: none;
        background-color: var(--color-text-tertiary);
        mask-image: url('/vanilla/ui/menu/circle-gauge.svg');
        mask-size: 24px;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/menu/circle-gauge.svg');
        -webkit-mask-size: 24px;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .cruise.selected {
        background-color: var(--color-bg-selector-hover);
      }

      .cruise.selected .icon {
        background-color: var(--color-cruise-active);
      }

      /* Hover-only — the sidebar is desktop-only, but a touch-only device the
         mobile check doesn't catch still renders it, and there a tap would
         leave the button lit and its card open after the finger lifts. Same
         gate as aircraft- and upgrade-selector. */
      @media (hover: hover) {
        .cruise:hover {
          background-color: var(--color-bg-selector-hover);
        }
        .cruise:hover .tooltip {
          display: block;
        }
      }

      /* The tooltip shell (.tooltip / .arrow / .header / .item / .desc / .clickto)
         is the shared selectorTooltip fragment; width comes from --tooltip-width
         on :host above. */
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** The hover tooltip is informational (cursor: default) but sits inside the
   *  clickable .cruise button, so a click on the card would otherwise bubble up
   *  and toggle cruise. Swallow it. Click only — hover still bubbles, so moving
   *  onto the card to read it keeps it open. Same guard as aircraft-selector
   *  and upgrade-selector. */
  private stopTooltipClick = (e: Event) => {
    e.stopPropagation();
  };

  override render() {
    const isActive = this.uiState.cruiseMode;
    // Live keybind label so the hint tracks remaps; omit the key if unbound.
    const cruiseKey = this.uiState.keybinds.bindByName['CRUISE'];

    return html`
      <div class="cruise ${isActive ? 'selected' : ''}" @click=${this.handleClick}>
        <div class="icon"></div>
        <div class="tooltip" @click=${this.stopTooltipClick}>
          <div class="arrow"></div>
          <div class="header">Cruise Mode</div>
          <div class="item">
            <div class="desc">
              Automatically advance at full speed without holding the throttle. Pressing forward or back cancels it.
            </div>
          </div>
          <div class="item">
            <div class="clickto">${cruiseKey ? `Click or press ${cruiseKey} to toggle` : 'Click to toggle'}</div>
          </div>
        </div>
      </div>
    `;
  }

  /** Handle cruise button click. Clicks like the other sidebar buttons; the
   *  keyboard toggle and the touch-controls button stay silent, as key and
   *  in-play touch actions do elsewhere. */
  private handleClick() {
    this.gameApi.playClick();
    this.gameApi.setCruiseMode(!this.uiState.cruiseMode);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cruise-selector': CruiseSelector;
  }
}
